<template>
  <teleport to="body">
    <div v-if="decouverte.enVisite && etape" class="pointer-events-none fixed inset-0 z-[70]">
      <!--
        LE VOILE : un SVG plein écran, percé d'un trou arrondi sur la cible. Le
        trou GLISSE d'un élément à l'autre (GSAP) : l'œil suit le mouvement au
        lieu de chercher où l'étape suivante l'emmène. Sans cible, le trou se
        referme au centre et le voile bloque la page (accueil, conclusion) ;
        avec une cible, la page reste cliquable — c'est tout l'intérêt des
        étapes « à vous de jouer ».
      -->
      <svg
        v-show="!modaleOuverte"
        class="absolute inset-0 h-full w-full"
        :class="rect ? '' : 'pointer-events-auto'"
        aria-hidden="true"
      >
        <defs>
          <mask :id="idMasque">
            <rect width="100%" height="100%" fill="white" />
            <rect ref="trouRef" rx="12" ry="12" fill="black" />
          </mask>
        </defs>
        <rect ref="voileRef" width="100%" height="100%" fill="rgba(13, 19, 42, 0.62)" :mask="`url(#${idMasque})`" />
      </svg>

      <!-- L'anneau doré autour de la cible ; il bat quand l'étape attend un geste. -->
      <div v-show="rect && !modaleOuverte" ref="anneauRef" class="absolute rounded-xl ring-2 ring-sunflower-gold" aria-hidden="true" />

      <div
        v-show="!modaleOuverte"
        ref="bulleRef"
        role="dialog"
        :aria-label="etape.titre"
        tabindex="-1"
        class="pointer-events-auto fixed left-0 top-0 w-[min(23rem,calc(100vw-2rem))] rounded-2xl bg-white p-5 shadow-2xl outline-none ring-1 ring-black/5 dark:bg-alt-dark-surface dark:text-altoneo-50 dark:ring-white/10"
      >
        <div ref="contenuRef">
          <div class="mb-2 flex items-center justify-between gap-3">
            <span class="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wider text-steel-azure dark:text-alt-dark-link">
              <span aria-hidden="true">{{ chapitre?.icone }}</span>{{ chapitre?.titre }}
            </span>
            <span class="shrink-0 text-xs tabular-nums text-slate-600 dark:text-alt-dark-muted">{{ decouverte.etape + 1 }} / {{ total }}</span>
          </div>

          <h3 class="mb-1.5 text-base font-semibold text-space-indigo dark:text-altoneo-50">{{ etape.titre }}</h3>
          <p class="text-sm leading-relaxed text-slate-600 dark:text-alt-dark-muted">{{ etape.texte }}</p>

          <p v-if="etape.consigne" class="mt-3 flex gap-2 rounded-lg bg-sunflower-gold/15 px-3 py-2 text-xs font-medium text-space-indigo dark:text-altoneo-50">
            <span aria-hidden="true">👉</span>
            <span>
              {{ etape.consigne }}
              <span v-if="etape.avance" class="block font-normal text-slate-500 dark:text-alt-dark-muted">La visite reprend d'elle-même.</span>
            </span>
          </p>
        </div>

        <div class="mt-4 h-1 overflow-hidden rounded-full bg-slate-100 dark:bg-alt-dark-raised">
          <div ref="barreRef" class="h-full rounded-full bg-gradient-to-r from-steel-azure to-sunflower-gold" style="width: 0" />
        </div>

        <div class="mt-4 flex items-center justify-between gap-2">
          <button type="button" class="text-xs font-medium text-slate-600 hover:text-slate-800 dark:text-alt-dark-muted dark:hover:text-altoneo-50" @click="decouverte.fermerVisite()">
            Quitter la visite
          </button>
          <div class="flex items-center gap-2">
            <AltButton v-if="decouverte.etape > 0" variant="ghost" size="sm" @click="decouverte.precedente()">Précédent</AltButton>
            <AltButton size="sm" @click="suivante">{{ derniere ? 'Terminer' : 'Suivant' }}</AltButton>
          </div>
        </div>
        <p class="mt-3 hidden text-[10px] text-slate-600 dark:text-alt-dark-muted sm:block">← → pour naviguer · Échap pour quitter</p>
      </div>
    </div>
  </teleport>
</template>

<script setup lang="ts">
import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useDecouverteStore } from '@/stores/decouverte';
import { useModeDemo } from '@/composables/useModeDemo';
import { garantirFin, gsap, mouvementReduit } from '@/lib/mouvement';

/**
 * LE MOTEUR DE LA VISITE — porté de l'outil IGD (VisiteGuidee.vue), lui-même
 * porté de Plans d'actions (TourOverlay.vue), avec ses règles d'usage : la bulle ne sort jamais de l'écran, elle se cache
 * derrière une fenêtre de l'application, et une étape « à vous de jouer »
 * avance seule quand le geste est fait. En plus :
 *  * le mouvement (GSAP) : le trou du voile glisse vers la cible, la bulle la
 *    suit, l'anneau bat quand un geste est attendu, un éclat salue la fin ;
 *  * le RÔLE : une étape peut faire endosser un autre rôle de la démo ;
 *  * le clavier : ← → pour naviguer, Échap pour quitter.
 */

const decouverte = useDecouverteStore();
const modeDemo = useModeDemo();
const route = useRoute();
const router = useRouter();

const idMasque = `bt-visite-${Math.random().toString(36).slice(2, 8)}`;
const trouRef = ref<SVGRectElement | null>(null);
const voileRef = ref<SVGRectElement | null>(null);
const anneauRef = ref<HTMLElement | null>(null);
const bulleRef = ref<HTMLElement | null>(null);
const contenuRef = ref<HTMLElement | null>(null);
const barreRef = ref<HTMLElement | null>(null);

const rect = ref<DOMRect | null>(null);
/**
 * Une modale cache la visite quand l'étape vise ce qu'il y a DERRIÈRE elle
 * (on laisse l'utilisateur faire le geste demandé). Elle reste visible si
 * l'étape vise le contenu de la modale (les champs de la nouvelle demande) ou
 * n'a pas encore de cible — sinon on resterait bloqué sans bulle.
 */
const modaleOuverte = ref(false);

const etape = computed(() => decouverte.etapeCourante);
const chapitre = computed(() => decouverte.chapitre);
const total = computed(() => chapitre.value?.etapes.length ?? 0);
const derniere = computed(() => decouverte.etape >= total.value - 1);

const reduit = mouvementReduit();
const PAD = 8;
const MARGE = 16;
const ECART = 14;

/* ------------------------------------------------------- Géométrie */

/** Le trou (et l'anneau) : un objet que GSAP interpole, recopié dans le SVG. */
const trou = { x: window.innerWidth / 2, y: window.innerHeight / 2, w: 0, h: 0 };

function peindreTrou(): void {
  trouRef.value?.setAttribute('x', String(trou.x));
  trouRef.value?.setAttribute('y', String(trou.y));
  trouRef.value?.setAttribute('width', String(Math.max(0, trou.w)));
  trouRef.value?.setAttribute('height', String(Math.max(0, trou.h)));
  if (anneauRef.value) {
    Object.assign(anneauRef.value.style, { left: `${trou.x}px`, top: `${trou.y}px`, width: `${trou.w}px`, height: `${trou.h}px` });
  }
}

function cibleTrou(r: DOMRect | null) {
  return r
    ? { x: r.left - PAD, y: r.top - PAD, w: r.width + PAD * 2, h: r.height + PAD * 2 }
    : { x: window.innerWidth / 2, y: window.innerHeight / 2, w: 0, h: 0 };
}

function deplacerTrou(anime: boolean): void {
  gsap.killTweensOf(trou);
  const but = cibleTrou(rect.value);
  if (!anime || reduit) {
    Object.assign(trou, but);
    peindreTrou();
    return;
  }
  garantirFin(gsap.to(trou, { ...but, duration: 0.55, ease: 'power3.inOut', onUpdate: peindreTrou }));
}

/** Où poser la bulle : sous la cible si elle y tient, sinon au-dessus ; toujours dans l'écran. */
function positionBulle(): { left: number; top: number } {
  const vw = window.innerWidth;
  const vh = window.innerHeight;
  const bw = bulleRef.value?.offsetWidth ?? Math.min(368, vw - 2 * MARGE);
  const bh = bulleRef.value?.offsetHeight ?? 220;
  const bornerX = (x: number): number => Math.max(MARGE, Math.min(x, vw - bw - MARGE));
  const bornerY = (y: number): number => Math.max(MARGE, Math.min(y, Math.max(MARGE, vh - bh - MARGE)));
  const r = rect.value;
  if (!r) return { left: bornerX((vw - bw) / 2), top: bornerY((vh - bh) / 2) };
  const place = etape.value?.placement ?? 'auto';
  const dessous = vh - r.bottom;
  const dessus = r.top;
  const besoin = bh + ECART + MARGE;
  const enDessous = place === 'bas' ? true : place === 'haut' ? false : dessous >= besoin || dessous >= dessus;
  // Une cible haute comme l'écran (menu latéral) : la bulle se met à côté.
  if (dessous < besoin && dessus < besoin && r.right + ECART + bw + MARGE <= vw) {
    return { left: r.right + ECART, top: bornerY(r.top + r.height / 2 - bh / 2) };
  }
  return {
    left: bornerX(r.left + r.width / 2 - bw / 2),
    top: bornerY(enDessous ? r.bottom + ECART : r.top - ECART - bh),
  };
}

let premiereApparition = true;

function placerBulle(anime: boolean): void {
  const b = bulleRef.value;
  if (!b) return;
  const { left, top } = positionBulle();
  if (!anime || reduit || premiereApparition) {
    gsap.set(b, { x: left, y: top });
    if (premiereApparition && !reduit) {
      garantirFin(gsap.fromTo(b, { autoAlpha: 0, scale: 0.94 }, { autoAlpha: 1, scale: 1, duration: 0.4, ease: 'back.out(1.7)' }));
    } else gsap.set(b, { autoAlpha: 1, scale: 1 });
    premiereApparition = false;
    return;
  }
  garantirFin(gsap.to(b, { x: left, y: top, duration: 0.5, ease: 'power3.inOut' }));
  if (contenuRef.value) {
    garantirFin(gsap.fromTo(contenuRef.value, { opacity: 0, y: 6 }, { opacity: 1, y: 0, duration: 0.3, delay: 0.15, ease: 'power2.out' }));
  }
}

function animerProgression(): void {
  if (!barreRef.value || !total.value) return;
  const largeur = `${Math.round(((decouverte.etape + 1) / total.value) * 100)}%`;
  if (reduit) barreRef.value.style.width = largeur;
  else garantirFin(gsap.to(barreRef.value, { width: largeur, duration: 0.45, ease: 'power2.out' }));
}

/* ----------------------------------------------- L'anneau qui bat */

let battement: gsap.core.Tween | null = null;

function battre(): void {
  battement?.kill();
  battement = null;
  if (!anneauRef.value) return;
  anneauRef.value.style.boxShadow = '';
  if (reduit || !etape.value?.consigne || !rect.value) return;
  battement = gsap.fromTo(
    anneauRef.value,
    { boxShadow: '0 0 0 0 rgba(249, 178, 51, 0.6)' },
    { boxShadow: '0 0 0 14px rgba(249, 178, 51, 0)', duration: 1.3, ease: 'power1.out', repeat: -1 },
  );
}

/* ------------------------------------------------- Trouver la cible */

let essaiEnCours = 0;

/**
 * La cible GRANDIT souvent après avoir été trouvée (une liste qui finit de
 * charger) : on l'observe, et le trou suit sa taille au lieu de rester sur
 * celle du squelette de chargement.
 */
const observateurTaille = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(() => suivreCible()) : null;
function observerCible(el: HTMLElement | null): void {
  observateurTaille?.disconnect();
  if (el) observateurTaille?.observe(el);
}

/** Un élément présent MAIS invisible (menu masqué sur téléphone) ne compte pas. */
function visible(el: Element | null | undefined): el is HTMLElement {
  if (!el) return false;
  const r = el.getBoundingClientRect();
  return r.width > 0 && r.height > 0;
}

/** La première occurrence VISIBLE : une même clé peut marquer la version bureau et la version téléphone. */
function trouverVisible(sel: string): HTMLElement | null {
  return Array.from(document.querySelectorAll(sel)).find(visible) ?? null;
}

async function localiserCible(): Promise<void> {
  const sel = etape.value?.cible;
  const numero = ++essaiEnCours;
  observerCible(null);
  if (!sel) {
    rect.value = null;
    return;
  }
  // Budget ~4 s : une page ne peint parfois sa cible qu'après ses données.
  for (let i = 0; i < 28; i += 1) {
    if (numero !== essaiEnCours) return;
    const el = trouverVisible(sel);
    if (el) {
      el.scrollIntoView({ block: 'center', behavior: reduit ? 'auto' : 'smooth' });
      await new Promise((r) => setTimeout(r, reduit ? 0 : 260));
      if (numero !== essaiEnCours) return;
      const trouve = trouverVisible(sel);
      rect.value = trouve?.getBoundingClientRect() ?? null;
      observerCible(trouve);
      return;
    }
    await new Promise((r) => setTimeout(r, 150));
  }
  if (numero === essaiEnCours) rect.value = null; // cible absente : bulle centrée
}

/* ------------------------------------------ Avance automatique */

let avancee = false;
let modaleVue = false;
let routeArmee: string | null = null;
let presenteAuDepart = false;

/** AltModal verrouille le défilement du body à l'ouverture : c'est le signal d'une fenêtre ouverte. */
const modaleDetectee = (): boolean => document.body.style.overflow === 'hidden';

function avancer(): void {
  if (avancee) return;
  avancee = true;
  decouverte.suivante();
}

/** La cible de l'étape est-elle présente, et derrière la modale ? */
const cibleDerriereModale = (): boolean => {
  const sel = etape.value?.cible;
  const el = sel ? trouverVisible(sel) : null;
  return !!el && !el.closest('[aria-modal="true"]');
};

function scruter(): void {
  const modale = modaleDetectee();
  modaleOuverte.value = modale && cibleDerriereModale();
  const a = etape.value?.avance;
  if (!a) return;
  if (a.type === 'apparait') {
    if (!presenteAuDepart && document.querySelector(a.selecteur)) avancer();
  } else if (a.type === 'modaleFermee') {
    if (modale) modaleVue = true;
    else if (modaleVue) avancer();
  }
}

/* ---------------------------------------------- Appliquer l'étape */

async function appliquer(): Promise<void> {
  const e = etape.value;
  if (!e) return;
  avancee = false;
  modaleVue = false;
  routeArmee = null;
  battement?.kill();
  if (e.persona && e.persona !== decouverte.persona) await modeDemo.changerPersona(e.persona);
  if (e.route && route.path !== e.route) await router.push(e.route);
  // Armé DÈS que l'étape a ouvert son écran, pas après la recherche de la cible :
  // un geste rapide (envoyer un formulaire déjà rempli) passerait sinon inaperçu.
  routeArmee = route.path;
  await nextTick();
  await localiserCible();
  await nextTick();
  deplacerTrou(true);
  placerBulle(true);
  animerProgression();
  battre();
  bulleRef.value?.focus({ preventScroll: true });
  presenteAuDepart = e.avance?.type === 'apparait' ? !!document.querySelector(e.avance.selecteur) : false;
  scruter();
}

/** Suit la cible quand la page défile ou que la fenêtre change de taille — sans animation. */
function suivreCible(): void {
  const sel = etape.value?.cible;
  if (!sel) {
    placerBulle(false);
    return;
  }
  const el = trouverVisible(sel);
  if (el) rect.value = el.getBoundingClientRect();
  deplacerTrou(false);
  placerBulle(false);
}

/* ---------------------------------------------- Fin de chapitre */

/** Un éclat de confettis aux couleurs de la charte, là où était la bulle. */
function eclat(): void {
  if (reduit || !bulleRef.value) return;
  const r = bulleRef.value.getBoundingClientRect();
  const hote = document.createElement('div');
  hote.setAttribute('aria-hidden', 'true');
  Object.assign(hote.style, { position: 'fixed', left: `${r.left + r.width / 2}px`, top: `${r.top + r.height / 2}px`, zIndex: '80', pointerEvents: 'none' });
  document.body.appendChild(hote);
  const couleurs = ['#f9b233', '#2d519f', '#f07f40', '#e83a5d', '#fde38d', '#8b5cf6'];
  const pieces = Array.from({ length: 28 }, (_, i) => {
    const p = document.createElement('span');
    Object.assign(p.style, {
      position: 'absolute',
      width: `${6 + (i % 3) * 2}px`,
      height: `${4 + (i % 2) * 4}px`,
      borderRadius: i % 4 === 0 ? '9999px' : '2px',
      background: couleurs[i % couleurs.length],
    });
    hote.appendChild(p);
    return p;
  });
  gsap
    .timeline({ onComplete: () => hote.remove() })
    .to(pieces, {
      x: () => gsap.utils.random(-190, 190),
      y: () => gsap.utils.random(-170, 60),
      rotation: () => gsap.utils.random(-260, 260),
      duration: 0.7,
      ease: 'power3.out',
    })
    .to(pieces, { y: '+=90', opacity: 0, duration: 0.6, ease: 'power1.in' }, '-=0.2');
}

function suivante(): void {
  if (derniere.value) eclat();
  decouverte.suivante();
}

/* -------------------------------------------------------- Clavier */

function auClavier(ev: KeyboardEvent): void {
  if (!decouverte.enVisite || modaleOuverte.value) return;
  const cible = ev.target as HTMLElement | null;
  const saisie = !!cible && (/^(INPUT|TEXTAREA|SELECT)$/.test(cible.tagName) || cible.isContentEditable);
  // Dans un champ, les flèches déplacent le curseur et Échap ferme une liste de
  // suggestions : ces touches appartiennent au champ, pas à la visite.
  if (saisie) return;
  // Une modale ouverte garde Échap pour elle : un seul geste ne ferme pas deux choses.
  if (ev.key === 'Escape' && !modaleDetectee()) decouverte.fermerVisite();
  else if (ev.key === 'ArrowRight') suivante();
  else if (ev.key === 'ArrowLeft') decouverte.precedente();
}

/* --------------------------------------------------------- Montage */

watch(
  () => [decouverte.chapitreId, decouverte.etape, decouverte.enVisite] as const,
  ([, , enVisite], avant) => {
    if (enVisite && etape.value) {
      if (!avant?.[2]) premiereApparition = true;
      void nextTick(() => void appliquer());
    } else {
      rect.value = null;
      modaleOuverte.value = false;
      battement?.kill();
      observerCible(null);
      essaiEnCours += 1;
    }
  },
  { immediate: true },
);

watch(
  () => route.path,
  (p) => {
    if (etape.value?.avance?.type === 'route' && routeArmee !== null && p !== routeArmee) avancer();
  },
);

let observateurDom: MutationObserver | null = null;
let observateurBody: MutationObserver | null = null;

onMounted(() => {
  observateurDom = new MutationObserver(() => scruter());
  observateurDom.observe(document.body, { childList: true, subtree: true });
  observateurBody = new MutationObserver(() => scruter());
  observateurBody.observe(document.body, { attributes: true, attributeFilter: ['style'] });
  window.addEventListener('scroll', suivreCible, true);
  window.addEventListener('resize', suivreCible);
  window.addEventListener('keydown', auClavier);
});

onBeforeUnmount(() => {
  observateurDom?.disconnect();
  observateurBody?.disconnect();
  observateurTaille?.disconnect();
  window.removeEventListener('scroll', suivreCible, true);
  window.removeEventListener('resize', suivreCible);
  window.removeEventListener('keydown', auClavier);
  battement?.kill();
  gsap.killTweensOf(trou);
});
</script>
